import React, { useEffect, useState } from 'react'
import { Modal, DatePicker, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment, { Moment } from 'moment'
import { observer } from 'mobx-react-lite'
import { Button } from 'shared/components'
import { ReadDailySummaryDto } from 'api-client'
import { ReportsStore } from 'store/reports.store'
import { DailySummaryStore } from 'store/daily-summary.store'

const { RangePicker } = DatePicker

interface DailyExpensesModalProps {
  open: boolean
  onClose: () => void
}

const columns: ColumnsType<ReadDailySummaryDto> = [
  {
    title: 'Date',
    key: 'date',
    dataIndex: 'summaryDate',
    render: (value?: string) => (value ? moment(value).format('DD.MM.YYYY') : '—'),
  },
  {
    title: 'Total',
    key: 'total',
    dataIndex: 'expenses',
  },
  {
    title: 'Notes',
    key: 'notes',
    dataIndex: 'notes',
    render: (value?: string) => value || '—',
  },
]

export const DailyExpensesModal = observer(({ open, onClose }: DailyExpensesModalProps) => {
  const [from, setFrom] = useState<Moment>(moment().startOf('month'))
  const [to, setTo] = useState<Moment>(moment().endOf('month'))
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    if (open) {
      ReportsStore.loadDailySummariesRange(
        from.clone().startOf('day').toISOString(),
        to.clone().endOf('day').toISOString()
      )
    }
  }, [open, from, to])

  const download = async () => {
    setDownloading(true)
    try {
      await DailySummaryStore.downloadCsv(
        from.clone().startOf('day').toISOString(),
        to.clone().endOf('day').toISOString()
      )
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Modal title="Daily expenses" open={open} onCancel={onClose} width={860} footer={null}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <RangePicker
          format="DD.MM.YYYY"
          value={[from, to]}
          onChange={(values) => {
            const [f, t] = values ?? [null, null]
            if (f) setFrom(f)
            if (t) setTo(t)
          }}
        />
        <Button onClick={download} disabled={downloading}>
          {downloading ? 'Downloading...' : 'Download CSV'}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={ReportsStore.dailySummariesRange}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => (
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Expense</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(record.items ?? []).map((item) => (
                  <tr key={`${item.title}-${item.amount}`}>
                    <td>{item.title}</td>
                    <td style={{ textAlign: 'right' }}>{item.amount ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ),
        }}
      />
    </Modal>
  )
})
